/**
 * A song's PARTS (2026-10-21, operator: "the whole sections thing is confusing, needs to be simpler and more
 * intuitive"; chose "the song IS the sections", parts living on the song; study `public/sections-study.html`).
 *
 * The song is cut into parts: every bar from 1 to its last is in exactly one part, a part is named (INTRO, VERSE 2...)
 * or not yet, and a timeline that follows its song has one section per part, in song order, at its length
 * (`sectionPlan`). On the wire nothing changed: the song's section marks (`TestTrackSectionMark`) are the NAMED parts,
 * so OPBridge still plays each section's mark by its name.
 *
 * Numbers are never typed: a part stores its kind, and `renumber` names them (one VERSE is "VERSE"; two are "VERSE 1"
 * and "VERSE 2", in song order). Every edit here returns new parts, already renumbered. Pure: no stores.
 */
import type { TestTrackSectionMark } from '../opbridge/httpClient.ts';

export interface SongPart { from: number; to: number; name: string | null }

/** The five every song offers (DEC-275), in the order the menu and keys 1-5 give them. */
export const PART_KINDS = ['INTRO', 'VERSE', 'PRE-CHORUS', 'CHORUS', 'BRIDGE'] as const;
/** Offered under Other. */
export const OTHER_KINDS = ['SOLO', 'INSTRUMENTAL', 'BREAKDOWN', 'DROP', 'BUILD', 'OUTRO'] as const;

const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
/** A name's kind: its number dropped, upper case, a known kind spelt the usual way ("Pre chorus 2" -> PRE-CHORUS). */
export function kindOf(name: string): string {
  const base = name.trim().replace(/\s*\d+\s*$/, '').trim().toUpperCase();
  return [...PART_KINDS, ...OTHER_KINDS].find((k) => norm(k) === norm(base)) ?? base;
}

/** Names from kinds: a kind used once is bare, more than once numbered 1, 2... in song order. */
export function renumber(parts: readonly SongPart[]): SongPart[] {
  const count = new Map<string, number>();
  for (const p of parts) if (p.name) { const k = kindOf(p.name); count.set(k, (count.get(k) ?? 0) + 1); }
  const seen = new Map<string, number>();
  return parts.map((p) => {
    if (!p.name) return { ...p };
    const k = kindOf(p.name);
    const n = (seen.get(k) ?? 0) + 1;
    seen.set(k, n);
    return { ...p, name: (count.get(k) ?? 0) > 1 ? `${k} ${n}` : k };
  });
}

/** Unnamed neighbours join (one quiet stretch), then the names are renumbered. */
function tidy(parts: readonly SongPart[]): SongPart[] {
  const out: SongPart[] = [];
  for (const p of parts) {
    const l = out[out.length - 1];
    if (l && !l.name && !p.name) l.to = p.to; else out.push({ ...p });
  }
  return renumber(out);
}

/** The song's marks as parts covering bars 1..`bars`, names as stored (a stretch no mark covers is unnamed). Marks
 *  that overlap (possible before parts existed) are cut so each bar has one part: the earlier mark keeps its bars. */
export function partsFromMarks(marks: readonly TestTrackSectionMark[] | undefined, bars: number): SongPart[] {
  const last = Math.max(1, Math.floor(bars));
  const out: SongPart[] = [];
  let at = 1;
  for (const m of [...(marks ?? [])].sort((a, b) => a.fromBar - b.fromBar || a.toBar - b.toBar)) {
    const from = Math.max(at, Math.round(m.fromBar));
    const to = Math.min(last, Math.round(m.toBar));
    if (to < from) continue;
    if (from > at) out.push({ from: at, to: from - 1, name: null });
    out.push({ from, to, name: m.name });
    at = to + 1;
  }
  if (at <= last) out.push({ from: at, to: last, name: null });
  return out;
}

/** The marks to store: the named parts. */
export function marksFromParts(parts: readonly SongPart[]): TestTrackSectionMark[] {
  return parts.filter((p) => p.name).map((p) => ({ name: p.name!, fromBar: p.from, toBar: p.to }));
}

export const partAt = (parts: readonly SongPart[], bar: number) => parts.findIndex((p) => bar >= p.from && bar <= p.to);

function cut(parts: SongPart[], bar: number): number {
  const i = partAt(parts, bar);
  if (i < 0 || parts[i]!.from === bar) return i;
  const p = parts[i]!;
  parts.splice(i, 1, { ...p, to: bar - 1 }, { ...p, from: bar });
  return i + 1;
}

/** A new part starts at `bar`; the new one is unnamed (the operator names it). */
export function splitAt(parts: readonly SongPart[], bar: number): SongPart[] {
  const out = parts.map((p) => ({ ...p }));
  const i = partAt(out, bar);
  if (i < 0 || out[i]!.from === bar) return tidy(out);
  out.splice(i, 1, { ...out[i]!, to: bar - 1 }, { from: bar, to: out[i]!.to, name: null });
  return tidy(out);
}

/** Bars a..b become one part named `name` (or unnamed), replacing whatever was there. Returns the parts and the new
 *  part's index. */
export function markRange(parts: readonly SongPart[], a: number, b: number, name: string | null): { parts: SongPart[]; index: number } {
  const out = parts.map((p) => ({ ...p }));
  const last = out[out.length - 1]?.to ?? 1;
  const lo = Math.max(1, Math.min(a, b)); const hi = Math.min(last, Math.max(a, b));
  if (hi < last) cut(out, hi + 1);
  const i = cut(out, lo);
  const j = partAt(out, hi);
  out.splice(i, j - i + 1, { from: lo, to: hi, name });
  // A new part not named yet stays its own (it is being asked about); tidying would fold it into the unmarked song.
  const tidied = name ? tidy(out) : renumber(out);
  return { parts: tidied, index: partAt(tidied, lo) };
}

/** Name part `i` (null = not a part). */
export function setName(parts: readonly SongPart[], i: number, name: string | null): SongPart[] {
  return tidy(parts.map((p, k) => (k === i ? { ...p, name: name ? kindOf(name) : null } : p)));
}

/** Move the line between part `i` and part `i + 1` so the later one starts at `bar` (each keeps at least a bar). */
export function moveLine(parts: readonly SongPart[], i: number, bar: number): SongPart[] {
  const a = parts[i]; const b = parts[i + 1];
  if (!a || !b) return parts.map((p) => ({ ...p }));
  const at = Math.max(a.from + 1, Math.min(b.to, Math.round(bar)));
  return tidy(parts.map((p, k) => (k === i ? { ...p, to: at - 1 } : k === i + 1 ? { ...p, from: at } : p)));
}

/** How far named part `i` can slide: through unnamed song only, keeping its length. */
export function slideRange(parts: readonly SongPart[], i: number): { lo: number; hi: number } {
  const me = parts[i]!; const len = me.to - me.from + 1;
  let lo = me.from; let hi = me.from;
  for (let j = i - 1; j >= 0 && !parts[j]!.name; j--) lo = parts[j]!.from;
  for (let j = i + 1; j < parts.length && !parts[j]!.name; j++) hi = parts[j]!.to - len + 1;
  return { lo, hi };
}

/** Slide named part `i` to start at `from` (clamped to `slideRange`); the bars it leaves are unnamed. */
export function slide(parts: readonly SongPart[], i: number, from: number): { parts: SongPart[]; index: number } {
  const me = parts[i];
  if (!me?.name) return { parts: parts.map((p) => ({ ...p })), index: i };
  const { lo, hi } = slideRange(parts, i);
  const at = Math.max(lo, Math.min(hi, Math.round(from)));
  const len = me.to - me.from + 1;
  const cleared = tidy(parts.map((p, k) => (k === i ? { ...p, name: null } : p)));
  return markRange(cleared, at, at + len - 1, kindOf(me.name));
}

/** Join part `i` with the one before (`-1`) or after (`1`); the joined part keeps part `i`'s name, or the
 *  neighbour's when part `i` has none. */
export function join(parts: readonly SongPart[], i: number, dir: -1 | 1): SongPart[] {
  const j = i + dir; const me = parts[i]; const other = parts[j];
  if (!me || !other) return parts.map((p) => ({ ...p }));
  const merged: SongPart = { from: Math.min(me.from, other.from), to: Math.max(me.to, other.to), name: me.name ?? other.name };
  const out = parts.filter((_, k) => k !== i && k !== j);
  out.splice(Math.min(i, j), 0, merged);
  return tidy(out);
}

/** What a following timeline's sections are: unnamed bars before the first part are its lead-in (DEC-296), then one
 *  section per part in song order, an unnamed stretch between or after parts a section with no name (the song
 *  carries on there). Empty when the song has no named part: the timeline keeps its own sections. */
export function sectionPlan(parts: readonly SongPart[]): { leadIn: number; sections: Array<{ name: string; lengthBars: number }> } {
  if (!parts.some((p) => p.name)) return { leadIn: 0, sections: [] };
  const list = [...parts];
  let leadIn = 0;
  if (list[0] && !list[0].name) { leadIn = list[0].to - list[0].from + 1; list.shift(); }
  return { leadIn, sections: list.map((p) => ({ name: p.name ?? '', lengthBars: p.to - p.from + 1 })) };
}
